import { DEFAULT_ITEMS_PER_PAGE } from '@/constants/numberVars';
import { TCustomAxiosPaginationResponse, TInfinitePagination } from '@/services/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface IUseInfinitePaginationWithSearchParams<TData, TSearchFilterType, TSearchFilterText, TAdditionalParams> {
  query: (
    page: number,
    searchFilterType: TSearchFilterType,
    searchFilterText: TSearchFilterText,
    sortingColumn?: string,
    sortingDirection?: string,
    additionalParams?: TAdditionalParams,
  ) => Promise<TCustomAxiosPaginationResponse<TData>>;
  queryKey: string[];
  searchFilterType: TSearchFilterType;
  searchFilterText: TSearchFilterText;
  sortingColumn?: string;
  sortingDirection?: string;
}

/**
 *
 * @param query - the query to be executed. Must receive `page: number` param and return TCustomAxiosResponse<T>
 * @returns isLoading: boolean, data: T[], fetchNextPage: () => void, isFetchingNextPage: boolean, hasNextPage: boolean;
 */
export const useInfinitePaginationWithSearch = <
  T extends any[],
  TSearchFilterType,
  TSearchFilterText,
  TAdditionalParams,
>({
  query,
  queryKey,
  searchFilterType,
  searchFilterText,
  sortingColumn,
  sortingDirection,
}: IUseInfinitePaginationWithSearchParams<T, TSearchFilterType, TSearchFilterText, TAdditionalParams>) => {
  const { isLoading, data, refetch, isRefetching, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery<
    TCustomAxiosPaginationResponse<T>
  >({
    queryKey: [...queryKey, searchFilterText, searchFilterType],
    queryFn: async ({ pageParam: page }) => {
      const response = await query(page as number, searchFilterType, searchFilterText, sortingColumn, sortingDirection);
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, __, lastPageParam) => {
      const totalPages = lastPage.pagination?.totalPages;
      // Prefer the server's page count: the short-page check below requests one
      // extra, empty page whenever the total is an exact multiple of the page size.
      if (typeof totalPages === 'number') {
        return (lastPageParam as number) < totalPages ? (lastPageParam as number) + 1 : undefined;
      }
      if (lastPage.data.length < DEFAULT_ITEMS_PER_PAGE) {
        return undefined;
      }
      return (lastPageParam as number) + 1;
    },
  });

  const flattenData = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) as T;
  }, [data?.pages]);

  return {
    isLoading,
    data: flattenData,
    refetch,
    isRefetching,
    infinitePagination: {
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      totalItems: data?.pages[0]?.pagination?.totalItems,
      loadedItems: flattenData?.length,
      loadedPages: data?.pages.length,
      totalPages: data?.pages[0]?.pagination?.totalPages,
    } as TInfinitePagination,
  };
};
